import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import BackgroundEffects from '../../../components/layout/BackgroundEffects/BackgroundEffects'
import Header from '../../../components/layout/Header/Header'
import Card from '../../../components/common/Card/Card'
import Button from '../../../components/common/Button/Button'
import { getCurrentUser, logout, getUserData } from '../../../services/authService'
import { getUserSalarySlips, getAllSalarySlips, deleteSalarySlip } from '../../../services/salarySlipsService'
import UploadForm from '../components/UploadForm'
import SlipCard from '../components/SlipCard'

const SalarySlips = () => {
  const navigate = useNavigate()

  // Auth & User State
  const [user, setUser] = useState(null)
  const [userData, setUserData] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)

  // Salary Slips State
  const [salarySlips, setSalarySlips] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [deleteError, setDeleteError] = useState('')
  const [deleteWarning, setDeleteWarning] = useState('')

  // Load user data and salary slips
  useEffect(() => {
    loadUserAndSlips()
  }, [])

  const loadUserAndSlips = async () => {
    try {
      setLoading(true)
      setError('')

      const currentUser = getCurrentUser()
      if (!currentUser) {
        navigate('/login')
        return
      }

      setUser(currentUser)

      // Get user data from Firestore
      const userDataResult = await getUserData(currentUser.uid)

      if (!userDataResult.success) {
        setError('Failed to load user data')
        setLoading(false)
        return
      }

      setUserData(userDataResult.data)

      // Check if user is admin
      const adminStatus =
        userDataResult.data.role === 'super_admin' ||
        userDataResult.data.role === 'admin'
      setIsAdmin(adminStatus)

      // Load salary slips based on role
      if (adminStatus) {
        const result = await getAllSalarySlips()
        if (result.success) {
          setSalarySlips(result.data)
        } else {
          setError(result.error || 'Failed to load salary slips')
        }
      } else {
        const result = await getUserSalarySlips(userDataResult.data.employeeId)
        if (result.success) {
          setSalarySlips(result.data)
        } else {
          setError(result.error || 'Failed to load salary slips')
        }
      }
    } catch (error) {
      console.error('Load error:', error)
      setError('An error occurred while loading data')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    const result = await logout()
    if (result.success) {
      navigate('/login')
    }
  }

  const handleBack = () => {
    navigate('/dashboard')
  }

  const handleSlipUploaded = (newSlip) => {
    // Add new slip to the list
    setSalarySlips(prev => [newSlip, ...prev])
  }

  const handleDelete = async (docId, publicId) => {
    setDeleteError('')
    setDeleteWarning('')

    try {
      const result = await deleteSalarySlip(docId, publicId)

      if (result.success) {
        // Remove from list
        setSalarySlips(prev => prev.filter(slip => slip.id !== docId))

        if (result.warning) {
          setDeleteWarning(result.warning)
          setTimeout(() => setDeleteWarning(''), 7000)
        }
      } else {
        setDeleteError(result.error || 'Failed to delete salary slip')
        setTimeout(() => setDeleteError(''), 5000)
      }
    } catch (error) {
      console.error('Delete error:', error)
      setDeleteError('An error occurred while deleting')
      setTimeout(() => setDeleteError(''), 5000)
    }
  }

  const handleDownload = (url) => {
    // Open PDF in new tab
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  // Loading State
  if (loading) {
    return (
      <div className="psyber-container psyber-portal-container">
        <BackgroundEffects />
        <Header
          user={userData || user}
          onLogout={handleLogout}
          showBackButton={true}
          onBack={handleBack}
          title="Salary Slips"
        />
        <Card style={{ textAlign: 'center', padding: '3rem' }}>
          <i
            className="fas fa-spinner fa-spin"
            style={{
              fontSize: '3rem',
              color: 'var(--psyber-primary)',
              marginBottom: '1rem'
            }}
          ></i>
          <p style={{ color: 'var(--psyber-text-secondary)' }}>Loading salary slips...</p>
        </Card>
      </div>
    )
  }

  // Error State (Critical Error)
  if (error && salarySlips.length === 0) {
    return (
      <div className="psyber-container psyber-portal-container">
        <BackgroundEffects />
        <Header
          user={userData || user}
          onLogout={handleLogout}
          showBackButton={true}
          onBack={handleBack}
          title="Salary Slips"
        />
        <Card style={{ textAlign: 'center', padding: '3rem' }}>
          <i
            className="fas fa-exclamation-triangle"
            style={{
              fontSize: '3rem',
              color: 'var(--psyber-error)',
              marginBottom: '1rem'
            }}
          ></i>
          <h3 style={{ marginBottom: '1rem', color: 'var(--psyber-error)' }}>Error</h3>
          <p style={{ color: 'var(--psyber-text-secondary)', marginBottom: '2rem' }}>{error}</p>
          <Button variant="primary" icon="fas fa-redo" onClick={loadUserAndSlips}>
            Retry
          </Button>
        </Card>
      </div>
    )
  }

  // Empty State (No Slips)
  const EmptyState = () => (
    <div style={{ textAlign: 'center', padding: '3rem' }}>
      <i
        className="fas fa-inbox"
        style={{
          fontSize: '4rem',
          color: 'var(--psyber-text-muted)',
          marginBottom: '1.5rem',
          display: 'block'
        }}
      ></i>
      <h3 style={{ marginBottom: '0.5rem', color: 'var(--psyber-text)' }}>
        No Salary Slips Yet
      </h3>
      <p style={{ color: 'var(--psyber-text-secondary)' }}>
        {isAdmin
          ? 'Upload salary slips using the form above'
          : 'Your salary slips will appear here once uploaded by HR'}
      </p>
    </div>
  )

  return (
    <div className="psyber-container psyber-portal-container">
      <BackgroundEffects />

      <Header
        user={userData || user}
        onLogout={handleLogout}
        showBackButton={true}
        onBack={handleBack}
        title="Salary Slips"
      />

      {/* Admin Upload Section */}
      {isAdmin && (
        <Card
          style={{
            marginBottom: '2rem',
            background: 'linear-gradient(135deg, rgba(255, 107, 179, 0.05) 0%, rgba(138, 123, 255, 0.05) 100%)',
            border: '2px solid var(--psyber-primary)'
          }}
        >
          <UploadForm
            onUploadSuccess={handleSlipUploaded}
            currentUserUid={user?.uid}
            currentUserName={userData?.fullName || 'Admin'}
          />
        </Card>
      )}

      {/* Cloudinary Manual Deletion Notice */}
      {isAdmin && (
        <Card
          style={{
            marginBottom: '1.5rem',
            background: 'var(--psyber-info-bg)',
            border: '2px solid var(--psyber-info)'
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '1rem'
          }}>
            <i
              className="fas fa-info-circle"
              style={{
                fontSize: '1.5rem',
                color: 'var(--psyber-info)',
                marginTop: '0.25rem'
              }}
            ></i>
            <div style={{ flex: 1 }}>
              <h4 style={{
                margin: '0 0 0.5rem 0',
                color: 'var(--psyber-info)',
                fontSize: 'var(--psyber-font-size-base)',
                fontWeight: 600
              }}>
                Cloudinary Storage Notice
              </h4>
              <p style={{
                margin: 0,
                color: 'var(--psyber-text-secondary)',
                fontSize: 'var(--psyber-font-size-sm)',
                lineHeight: 1.6
              }}>
                ⚠️ <strong>Note:</strong> Deleted salary slips are removed from the portal but remain in Cloudinary storage.
                To permanently delete files, go to{' '}
                <a
                  href="https://console.cloudinary.com/console/media_library"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: 'var(--psyber-info)',
                    fontWeight: 600,
                    textDecoration: 'underline'
                  }}
                >
                  Cloudinary Media Library
                </a>
                {' '}→ salary_slips folder and delete manually.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Delete Error Message */}
      {deleteError && (
        <Card
          style={{
            marginBottom: '1.5rem',
            background: 'var(--psyber-error-bg)',
            border: '2px solid var(--psyber-error)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <i className="fas fa-exclamation-circle" style={{ color: 'var(--psyber-error)', fontSize: '1.5rem' }}></i>
            <p style={{ margin: 0, color: 'var(--psyber-error)', fontWeight: 600 }}>{deleteError}</p>
          </div>
        </Card>
      )}

      {/* Delete Warning Message */}
      {deleteWarning && (
        <Card
          style={{
            marginBottom: '1.5rem',
            background: 'var(--psyber-warning-bg)',
            border: '2px solid var(--psyber-warning)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <i className="fas fa-exclamation-triangle" style={{ color: 'var(--psyber-warning)', fontSize: '1.5rem' }}></i>
            <p style={{ margin: 0, color: 'var(--psyber-warning)', fontWeight: 600 }}>{deleteWarning}</p>
          </div>
        </Card>
      )}

      {/* Salary Slips Section */}
      <Card>
        {/* Section Header */}
        <div
          style={{
            marginBottom: '1.5rem',
            paddingBottom: '1rem',
            borderBottom: '2px solid var(--psyber-card-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '1rem'
          }}
        >
          <div>
            <h2 style={{
              margin: 0,
              fontSize: 'var(--psyber-font-size-2xl)',
              color: 'var(--psyber-text)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <i className="fas fa-file-invoice-dollar"></i>
              {isAdmin ? 'All Salary Slips' : 'My Salary Slips'}
            </h2>
            <p style={{
              margin: '0.5rem 0 0 0',
              fontSize: 'var(--psyber-font-size-sm)',
              color: 'var(--psyber-text-secondary)'
            }}>
              {isAdmin
                ? `Managing ${salarySlips.length} salary slip${salarySlips.length !== 1 ? 's' : ''} across all employees`
                : `You have ${salarySlips.length} salary slip${salarySlips.length !== 1 ? 's' : ''} available`}
            </p>
          </div>

          {/* Refresh Button */}
          <Button variant="ghost" icon="fas fa-sync" onClick={loadUserAndSlips}>
            Refresh
          </Button>
        </div>

        {/* Salary Slips Grid or Empty State */}
        {salarySlips.length === 0 ? (
          <EmptyState />
        ) : (
          <div
            className="psyber-grid"
            style={{
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '1.5rem'
            }}
          >
            {salarySlips.map((slip) => (
              <SlipCard
                key={slip.id}
                slip={slip}
                isAdmin={isAdmin}
                onDownload={handleDownload}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Help Section for Employees */}
      {!isAdmin && (
        <Card style={{ marginTop: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <i className="fas fa-question-circle"></i> Need Help?
          </h3>
          <div style={{ fontSize: 'var(--psyber-font-size-sm)', color: 'var(--psyber-text-secondary)', lineHeight: 1.6 }}>
            <p style={{ marginBottom: '0.75rem' }}>
              <strong>How to download:</strong> Click the "Download" button on any salary slip card to open the PDF in a new tab.
            </p>
            <p style={{ marginBottom: '0.75rem' }}>
              <strong>Missing a salary slip?</strong> Contact HR if you don't see a salary slip that should be available.
            </p>
            <p style={{ margin: 0 }}>
              <strong>Questions about your salary?</strong> Please reach out to the HR department at{' '}
              <a href="mailto:hello@psybernova.com" style={{ color: 'var(--psyber-primary)', textDecoration: 'none' }}>
                hello@psybernova.com
              </a>
            </p>
          </div>
        </Card>
      )}
    </div>
  )
}

export default SalarySlips
